import { ClientConfig, createClient } from 'next-sanity'
import { page, siteSettings, sections } from '@/data/sanity/fragments'
import {
  augmentSectionsWithOpenRoleCounts,
  formatSiteSettingsResponse,
  getSections,
  mergeSiteSettings,
} from '@/utils/sanity'
import { getHomepageSlug } from '@/utils/urls'
import { DOC_TYPES, REGIONS } from '@/data'
import { pageSitemapFields } from '@/data/sanity/fragments/_shared'
import { getFallbackRegionsByRegion, getEnabledRegionOrFallback } from '@/utils/regions'
import { redirect } from 'next/navigation'

let perspective = process.env.SANITY_PREVIEW_TOKEN === undefined ? 'published' : 'drafts'
if (process.env.NODE_ENV === 'development') {
  perspective = 'raw'
}

const SANITY_CONFIG: ClientConfig = {
  projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID,
  dataset: process.env.NEXT_PUBLIC_SANITY_DATASET,
  apiVersion: process.env.NEXT_PUBLIC_SANITY_API_VERSION,
  useCdn: process.env.SANITY_PREVIEW_TOKEN === undefined ? true : false,
  token: process.env.SANITY_PREVIEW_TOKEN,
  perspective: perspective as 'raw' | 'published' | 'drafts',
}

export const client = createClient(SANITY_CONFIG)

const revalidateDefaultPeriod = parseInt(process.env.REVALIDATE || '604800') // 1 week revalidation
const revalidate = process.env.NODE_ENV !== 'production' ? 0 : revalidateDefaultPeriod

export const getSiteSettings = async ({ isPreview, language }: { isPreview: boolean; language?: string }) => {
  if (!language) language = REGIONS.EN_US

  /* eslint-disable */
  const siteSettingsData: SanitySiteSettingsResponse = await client.fetch(
    `
      *[(
        _type == "siteSettingsMetadata" ||
        _type == "navigation" ||
        _type == "footer" ||
        _type == "globalNewsletterSignUpModal"
        )] {
        ${siteSettings.fields}
      }
  `,
    { isPreview: Boolean(isPreview), language: language.replace('-', '_') },
    {
      next: {
        revalidate,
        tags: ['siteSettingsMetadata', 'footer', 'navigation', 'globalNewsletterSignUpModal'],
      },
    },
  )

  /* eslint-enable */

  if (!siteSettingsData?.length) return null

  const formatted = formatSiteSettingsResponse(siteSettingsData)

  return formatted as SanitySiteSettingsFormatted
}

export const getPage = async ({
  slug,
  browserSlug,
  type = 'page',
  isPreview,
  region,
}: {
  slug: string
  browserSlug?: string
  type: string
  isPreview: boolean
  region?: string
}) => {
  if (!region) region = REGIONS.EN_US

  // Get fallback regions upfront
  const fallbackRegions = getFallbackRegionsByRegion(region)

  // Use optimized query that conditionally fetches content
  // This avoids 300KB limit by only fetching sections that exist
  const query = `
    *[_type == "${type}" && slug.current == "${slug}"] {
      ${page.getFields(fallbackRegions)}
    }
  `

  const response = (
    await client.fetch(
      query,
      { isPreview: Boolean(isPreview), language: region.replace('-', '_') },
      {
        next: {
          revalidate,
          tags: [
            'page',
            `${type}:${(slug === getHomepageSlug()) ? 'home' : slug}`,
            ...Object.values(DOC_TYPES).map(type => type),
          ],
        },
      },
    )
  )[0]

  if (!response || !siteSettings) return null

  // In production, we only fetch section references to avoid 300KB limit
  // Now we need to fetch the actual section data
  if (process.env.NODE_ENV === 'production' && response.sections) {
    let sectionsToFetch: string[] = []
    let targetRegion: string | null = null

    // Find which region has section references
    for (const regionKey of fallbackRegions) {
      if (response.sections[regionKey]?.length > 0) {
        sectionsToFetch = response.sections[regionKey]
        targetRegion = regionKey
        break // Use first region with content (fallback priority)
      }
    }

    if (sectionsToFetch.length > 0 && targetRegion) {
      // Discover which section component types this page actually uses so the
      // batched content query only inlines fragments for those types. Keeps the
      // Sanity POST body well under its 300 KB limit even as the section
      // catalog grows. Note the document-level `_type` is always "section"; the
      // actual component type is the inner `section[0]._type`.
      const sectionTypesResponse: Array<{ innerType: string | null }> = await client.fetch(
        '*[_id in $refs] { "innerType": section[0]._type }',
        { refs: sectionsToFetch },
        {
          next: {
            revalidate,
            tags: ['sections'],
          },
        },
      )
      const uniqueSectionTypes = Array.from(
        new Set(sectionTypesResponse.map(s => s.innerType).filter((t): t is string => Boolean(t))),
      )

      // Fetch sections in smaller batches to avoid 300KB limit
      const batchSize = 10
      const sectionsBatches = []

      for (let i = 0; i < sectionsToFetch.length; i += batchSize) {
        const batch = sectionsToFetch.slice(i, i + batchSize)
        const sectionsQuery = `
          *[_id in $refs] {
            ${sections.getFieldsForTypes(uniqueSectionTypes)}
          }
        `

        const sectionsData = await client.fetch(
          sectionsQuery,
          {
            refs: batch,
            isPreview: Boolean(isPreview),
            language: region.replace('-', '_'),
          },
          {
            next: {
              revalidate,
              tags: ['sections'],
            },
          },
        )

        sectionsBatches.push(...sectionsData)
      }

      // Create a map for quick lookup by _id
      const sectionsMap = new Map()
      sectionsBatches.forEach(section => {
        sectionsMap.set(section._id, section)
      })

      // Rebuild sections array in the original order
      const orderedSections = sectionsToFetch
        .map(refId => sectionsMap.get(refId))
        .filter(section => section) // Remove any missing sections

      // Replace section references with actual data in correct order
      response.sections[targetRegion] = orderedSections
    }
  }

  // Check if region is enabled or get a fallback
  const enabledRegion = getEnabledRegionOrFallback(region, response.enabledRegions)
  if (!enabledRegion) {
    // Redirect to same path with default region and to home page if en-us is disabled on this page
    const redirectTo = region === REGIONS.EN_US ? `/${REGIONS.EN_US}` : `/${REGIONS.EN_US}/${browserSlug || slug}`
    return redirect(redirectTo)
  }
  region = enabledRegion

  const merged = mergeSiteSettings(response, response.globalMetaData) as SanityPage

  // Get fallback regions for section processing
  const sectionsFallbackRegions = getFallbackRegionsByRegion(region)
  const newSections = getSections(merged, region, sectionsFallbackRegions)

  merged.sections = await augmentSectionsWithOpenRoleCounts(newSections)

  return merged as SanityPage
}

export const getAllPages = async (
  type = DOC_TYPES.PAGE,
  isPreview?: boolean,
  region?: string,
  isSitemapGeneration?: boolean,
) => {
  if (!region) region = REGIONS.EN_US

  const tags = isSitemapGeneration ? ['sitemap'] : [...Object.values(DOC_TYPES).map(type => type)]

  // Get fallback regions for the query
  const fallbackRegions = getFallbackRegionsByRegion(region)

  const docTypes = typeof DOC_TYPES === 'object' && Object.keys(DOC_TYPES).length > 0 ? Object.values(DOC_TYPES) : []
  const docTypesOrCondition = docTypes.map(type => `_type == "${type}"`).join(' || ')
  const sitemapQuery = ` *[${docTypesOrCondition}] {
      ${pageSitemapFields}
    }
  `
  const pagesQuery = `*[_type == "${type}"] {
      ${page.getFields(fallbackRegions)}
    }
  `

  const pages = await client.fetch(
    isSitemapGeneration ? sitemapQuery : pagesQuery,
    { isPreview: Boolean(isPreview), language: region.replace('-', '_') },
    {
      next: {
        revalidate,
        tags,
      },
    },
  )

  if (!pages.length) return null

  return pages as SanityPage[]
}

export const getAllSlugsForRegion = async (type: string = DOC_TYPES.PAGE, region: string) => {
  const query = `
    *[_type == "${type}" && defined(enabledRegions) && $language in enabledRegions] {
      "slug": slug,
    }
  `
  const response = await client.fetch(query, { language: region.replace('-', '_') }, {

    next: {
      revalidate: 604800, // 1 week
      tags: ['staticParams', type],
    },
  })


  if (response?.length) {
    return response.map((page: SanityPage) => ({
      slug: page.slug,
    }))
  }
}

// Optimized function for sitemap generation - fetches only necessary metadata
export const getAllPagesForSitemap = async (type: string = DOC_TYPES.PAGE) => {
  const pages: Array<SanityPageStaticParams> = []

  // Fetch only metadata needed for sitemap
  const query = `
    *[_type == "${type}"] {
      "slug": slug,
      "_type": _type,
      "_updatedAt": _updatedAt,
      enabledRegions,
      metadata {
        en_usMetadata {
          noindex
        },
        en_caMetadata {
          noindex
        },
        zh_usMetadata {
          noindex
        },
        zh_caMetadata {
          noindex
        }
      }
    }
  `

  const response = await client.fetch(
    query,
    {},
    {
      next: {
        revalidate: 604800, // 1 week
        tags: ['sitemap', type],
      },
    },
  )

  if (response?.length) {
    response.forEach((page: SanityPageStaticParams) => {
      if (page?.slug?.current) {
        const enabledRegions = page.enabledRegions || []
        // Normalize enabled regions to use hyphens for comparison
        const normalizedEnabledRegions = enabledRegions.map((r: string) => r.replace('_', '-'))

        const regionsToCheck = normalizedEnabledRegions.length > 0
          ? normalizedEnabledRegions.filter((r: string) => Object.values(REGIONS).includes(r as typeof REGIONS[keyof typeof REGIONS]))
          : Object.values(REGIONS)

        regionsToCheck.forEach((region: string) => {
          const regionKey = region.replace('-', '_')
          const metadata = (page.metadata as Record<string, Partial<SanityMetadata>>)?.[`${regionKey}Metadata`] || {}

          pages.push({
            slug: page.slug,
            region,
            _type: page._type,
            _updatedAt: page._updatedAt,
            isEnabled: normalizedEnabledRegions.includes(region) || normalizedEnabledRegions.length === 0,
            metadata,
          })
        })
      }
    })
  }

  return pages
}

// Legacy function - kept for backwards compatibility but redirects to sitemap version
export const getAllPagesForAllRegions = async (type: string = DOC_TYPES.PAGE) => {
  // Use the sitemap version which has the metadata needed
  return getAllPagesForSitemap(type)
}

// New function for region-based pages
export const getAllPagesForAllRegionsBkp = async (
  type = DOC_TYPES.PAGE,
  isPreview?: boolean,
  isSitemapGeneration?: boolean,
) => {
  let data: SanityPageWithRegion[] = []
  const slugTypeHashmap: { [key: string]: string } = {}

  for (const region of Object.values(REGIONS)) {
    const pageData = await getAllPages(type, isPreview, region, isSitemapGeneration)
    const newData: SanityPageWithRegion[] = []

    pageData?.forEach((page: SanityPage) => {
      if (slugTypeHashmap[`${page._type}_${page.slug.current}_${region}`]) return
      slugTypeHashmap[`${page._type}_${page.slug.current}_${region}`] = 'true'
      newData.push({ ...page, region })
    })

    data = [...data, ...newData]
  }

  if (!data.length) return []

  return data as SanityPageWithRegion[]
}

// Type to be added to types file
interface SanityPageWithRegion extends SanityPage {
  region: string
}
